import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";

interface RefreshOptions {
  showLoading?: boolean;
}

export function useOrdersTableRefresh<T>(
  fetchOrders: () => Promise<T[]>,
  setOrders: Dispatch<SetStateAction<T[]>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  onError?: (error: unknown) => void
) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Store onError in a ref so an inline callback passed by the caller
  // never causes refreshOrders to get a new reference and trigger infinite
  // re-renders via useEffect([refreshOrders]).
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  const refreshOrders = useCallback(
    async ({ showLoading = false }: RefreshOptions = {}) => {
      if (showLoading) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        const data = await fetchOrders();
        setOrders(data);
      } catch (error) {
        if (onErrorRef.current) {
          onErrorRef.current(error);
        }
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [fetchOrders, setLoading, setOrders] // onError intentionally omitted — accessed via ref
  );

  return { refreshOrders, isRefreshing };
}
